import { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, User, Building2 } from "lucide-react";
import { TenantMessage } from "@/hooks/use-property-management";
import { format } from "date-fns";

interface Props {
  messages: TenantMessage[];
  isLoading: boolean;
  portfolioProperties: { id: string; label: string }[];
  onSend: (msg: { pipeline_property_id: string; tenant_name: string; message: string; sender_type: string }) => void;
}

export function TenantChatTab({ messages, isLoading, portfolioProperties, onSend }: Props) {
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [newMessage, setNewMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Get unique tenants for the selected property
  const tenants = useMemo(() => {
    const names = new Set(messages.filter(m => !selectedProperty || m.pipeline_property_id === selectedProperty).map(m => m.tenant_name));
    return Array.from(names);
  }, [messages, selectedProperty]);

  // Filtered messages
  const filteredMessages = useMemo(() => {
    return messages.filter(m =>
      (!selectedProperty || m.pipeline_property_id === selectedProperty) &&
      (!selectedTenant || m.tenant_name === selectedTenant)
    );
  }, [messages, selectedProperty, selectedTenant]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [filteredMessages.length]);

  const handleSend = () => {
    if (!newMessage.trim() || !selectedProperty || !selectedTenant) return;
    onSend({
      pipeline_property_id: selectedProperty,
      tenant_name: selectedTenant,
      message: newMessage.trim(),
      sender_type: "landlord",
    });
    setNewMessage("");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[600px]">
      {/* Sidebar */}
      <Card className="shadow-soft flex flex-col">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Conversations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 flex-1">
          <div className="space-y-2">
            <Label className="text-xs">Property</Label>
            <Select value={selectedProperty} onValueChange={(v) => { setSelectedProperty(v); setSelectedTenant(""); }}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All properties" /></SelectTrigger>
              <SelectContent>
                {portfolioProperties.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ScrollArea className="flex-1">
            {tenants.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No conversations</p>
            ) : (
              <div className="space-y-1">
                {tenants.map(t => (
                  <button
                    key={t}
                    onClick={() => setSelectedTenant(t)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${selectedTenant === t ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
                  >
                    <User className="h-4 w-4" />
                    {t}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
          {selectedProperty && (
            <div className="pt-2 border-t">
              <Input
                placeholder="New tenant name..."
                className="h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.target as HTMLInputElement).value) {
                    setSelectedTenant((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="shadow-soft flex flex-col">
        {!selectedTenant ? (
          <CardContent className="flex flex-col items-center justify-center flex-1">
            <MessageSquare className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Select a conversation</p>
            <p className="text-xs text-muted-foreground/60">Choose a property and tenant to start messaging</p>
          </CardContent>
        ) : (
          <>
            <CardHeader className="border-b pb-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm">{selectedTenant}</CardTitle>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {filteredMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_type === "landlord" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 ${msg.sender_type === "landlord" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_type === "landlord" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {format(new Date(msg.created_at), "MMM d, h:mm a")}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>
            <div className="p-3 border-t flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <Button size="icon" onClick={handleSend} disabled={!newMessage.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
